import { FC } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomSwitch from "@dashboard/components/custom-switch/custom-switch";
import { partnerSelector } from "@dashboard/config/redux";
import { hasConfiguredStoreHours } from "@dashboard/modules/profile/utils/store-hours.utils";
import { PROFILE_ROUTES } from "@dashboard/modules/profile/profile.routes";
import { ServiceDurationOptionEnum } from "@dashboard/modules/products/constants/service.constants";
import { FormProps } from "@dashboard/modules/products/types/product-form.types";
import X from "@sumerkit/icons/x";
import { ServiceDurationWrapper } from "./service-duration.styled";

const DURATION_OPTIONS = [
  ServiceDurationOptionEnum.One,
  ServiceDurationOptionEnum.Two,
  ServiceDurationOptionEnum.Three,
  ServiceDurationOptionEnum.Other,
];

const ServiceDuration: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const partner = useSelector(partnerSelector);
  const { values, setFieldValue } = useFormikContext<FormProps>();

  const hasStoreHours = hasConfiguredStoreHours(
    partner?.userDetail?.scheduleConfiguration
  );
  const showScheduleCard = values.showScheduleInfo && !hasStoreHours;

  const handleToggle = (active: boolean) => {
    setFieldValue("serviceDurationToggle", active);
    if (!active) {
      setFieldValue("serviceDurationOption", ServiceDurationOptionEnum.None);
    } else if (
      values.serviceDurationOption === ServiceDurationOptionEnum.None
    ) {
      setFieldValue("serviceDurationOption", ServiceDurationOptionEnum.One);
    }
  };

  const handleOptionChange = (option: ServiceDurationOptionEnum) => {
    setFieldValue("serviceDurationOption", option);
  };

  const handleCustomTimeChange = (timeValue: string) => {
    const [hourStr, minuteStr] = timeValue.split(":");
    setFieldValue("serviceDurationHour", Number(hourStr) || 0);
    setFieldValue("serviceDurationMinute", Number(minuteStr) || 0);
  };

  const handleNavigateToStoreHours = () => {
    if (id) {
      navigate(`/${id}/${PROFILE_ROUTES.ROOT}/${PROFILE_ROUTES.STORE_HOURS}`);
    }
  };

  const handleCloseScheduleCard = () => {
    setFieldValue("showScheduleInfo", false);
  };

  const customTimeValue = `${String(values.serviceDurationHour ?? 0).padStart(2, "0")}:${String(values.serviceDurationMinute ?? 0).padStart(2, "0")}`;

  return (
    <ServiceDurationWrapper>
      <div className="duration-header">
        <span className="duration-title">
          {t("productComponent.service.duration.title")}
        </span>
        <CustomSwitch
          isActive={!!values.serviceDurationToggle}
          onChange={handleToggle}
        />
      </div>

      {values.serviceDurationToggle && (
        <>
          <div className="duration-options">
            {DURATION_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={`duration-chip ${
                  values.serviceDurationOption === option ? "active" : ""
                }`}
                onClick={() => handleOptionChange(option)}
              >
                {t(`productComponent.service.duration.options.${option}`)}
              </button>
            ))}
          </div>

          {values.serviceDurationOption === ServiceDurationOptionEnum.Other && (
            <div className="custom-duration">
              <label htmlFor="service-custom-duration">
                {t("productComponent.service.duration.customLabel")}
              </label>
              <input
                id="service-custom-duration"
                type="time"
                value={customTimeValue}
                onChange={(e) => handleCustomTimeChange(e.target.value)}
              />
            </div>
          )}
        </>
      )}

      {showScheduleCard && (
        <div className="schedule-card">
          <div className="schedule-card-content">
            <p>{t("productComponent.service.duration.scheduleCardText")}</p>
            <button
              type="button"
              className="schedule-card-button"
              onClick={handleNavigateToStoreHours}
            >
              {t("productComponent.service.duration.configureSchedule")}
            </button>
          </div>
          <button
            type="button"
            className="schedule-card-close"
            onClick={handleCloseScheduleCard}
            aria-label="Close"
          >
            <X width={16} height={16} />
          </button>
        </div>
      )}

      {hasStoreHours && (
        <button
          type="button"
          className="schedule-edit-link"
          onClick={handleNavigateToStoreHours}
        >
          {t("productComponent.service.duration.editSchedule")}
        </button>
      )}
    </ServiceDurationWrapper>
  );
};

export default ServiceDuration;
